import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accessibility } from '../../../utils/Accessibility/Accessibility';
import { FontAwesomeConfigurations } from '../../../utils/Configuration/FontAwesomeConfiguration';
import JUNOActionMenu from '../../components/JUNOActionMenu';
import JUNOSwitch from '../../components/JUNOSwitch';
import useOutsideAlerter from '../../hooks/useOutsideAlerter';
import './style.scss';

interface NotificationUserPreference {
  app_push: boolean | number;
  email: boolean | number;
  id: number;
  preference_id: number;
  site_id: number;
  user_id: number;
  web: boolean | number;
  web_push: boolean | number;
}

interface NotificationPreference {
  id: number;
  title: string;
  subtitle: string;
}

interface NotificationPrefModuleProps {
  preference: NotificationPreference;
  userPreferences: NotificationUserPreference[];
  handleChange: (aChange: boolean) => void;
  updateUserPreference: (preference: NotificationPreference, type: string, checked: boolean) => void;
}

const NotificationPrefModule: React.FC<NotificationPrefModuleProps> = ({ ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const userWebPushPref = props.userPreferences.filter(pref => {
    return pref.preference_id == props.preference.id;
  })[0]?.web_push;
  const userAppPushPref = props.userPreferences.filter(pref => {
    return pref.preference_id == props.preference.id;
  })[0]?.app_push;
  const userEmailPushPref = props.userPreferences.filter(pref => {
    return pref.preference_id == props.preference.id;
  })[0]?.email;

  const [switchState, setSwitchState] = useState(() => {
    return userWebPushPref == 1 || userAppPushPref == 1 || userEmailPushPref == 1;
  });
  const [webPush, setWebPush] = useState(userWebPushPref == 1);
  const [appPush, setAppPush] = useState(userAppPushPref == 1);
  const [emailPush, setEmailPush] = useState(userEmailPushPref == 1);
  const [subtitleState, setSubtitleState] = useState(props.preference.subtitle);

  const wrapperRef = useRef(null);
  const onClickOpen = e => {
    if (!e.target?.classList.contains('checkmark_span') && e.target.type != 'checkbox') {
      Accessibility.actionMenuOpen = !isOpen;
      setIsOpen(old => !old);
    }
  };
  useOutsideAlerter(wrapperRef, e => {
    // TODO this gets fired like 8x per click and the below stop doesnt affect it...however it does function properly
    e.stopPropagation();
    // console.log('react fired');
    setIsOpen(false);
  });

  useEffect(() => {
    if (App.CORDOVA) {
      /* no, leon says. this isn't how we do anything. don't globally affect a container because you want a panel to show differently.. affect the panel.
    also we don't want to affect the panel 
      if (isOpen) {
        $('#bottomnav').hide();
        $('#messages').css('height', '100%');
      } else {
        $('#bottomnav').show();
        $('#messages').css('height', 'calc(100% - 70px');
      }
      */
    }
  }, [isOpen]);

  useMemo(() => {
    if (!webPush && !appPush && !emailPush) {
      // all prefs are off, turn off the switch
      setSubtitleState('No notifications enabled');
      setSwitchState(false);
    } else {
      // otherwise something is on, turn on the switch and set the subtitle string
      setSwitchState(true);
      if ((webPush && appPush && emailPush) || ((webPush || appPush) && emailPush)) {
        // if all are on or either push is on
        setSubtitleState('Profile, email and push notifications enabled');
      } else if (webPush || appPush) {
        // either push
        setSubtitleState('Profile and push notifications enabled');
      } else if (emailPush) {
        // email
        setSubtitleState('Profile and email notifications enabled');
      }
    }
  }, [webPush, appPush, emailPush]);
  const actionMenuHeaders = [
    {
      order: 0,
      text: 'How do you want us to notify you?',
    },
  ];

  const actionMenuItems = [
    {
      header: 0,
      onclick: checked => {}, // web is always on
      id: 1,
      order: 0,
      disabled: true,
      text: 'Profile Notifications',
      icon: FontAwesomeConfigurations.ICON_STAR_SOLID,
    },
    {
      header: 0,
      onclick: checked => {
        props.updateUserPreference({ preference: props.preference, type: 'web_push', checked: checked });
        setWebPush(checked);
      },
      id: 2,
      order: 1,
      text: 'Website Push Notifications',
      isCheckbox: true,
      isChecked: webPush,
    },
    {
      header: 0,
      onclick: checked => {
        props.updateUserPreference({ preference: props.preference, type: 'app_push', checked: checked });
        setAppPush(checked);
      },
      id: 3,
      order: 2,
      text: 'Mobile Push Notifications',
      isCheckbox: true,
      isChecked: appPush,
    },
    {
      header: 0,
      onclick: checked => {
        props.updateUserPreference({ preference: props.preference, type: 'email', checked: checked });
        setEmailPush(checked);
      },
      id: 4,
      order: 3,
      text: 'Email Notifications',
      isCheckbox: true,
      isChecked: emailPush,
    },
  ];

  if (App.INSTANCE.isPhone()) {
    actionMenuHeaders[0].text = props.preference.title;
  }

  return (
    <div className="notification_pref_module">
      <JUNOSwitch
        toggle_side={'left'}
        id={props.preference.id}
        switch_title={props.preference.title}
        switch_subtitle={subtitleState}
        initialState={switchState}
        handleChange={switchChange => {
          setSwitchState(old => !old);
          setWebPush(switchChange);
          setAppPush(switchChange);
          setEmailPush(switchChange);
          props.handleChange(switchChange);
        }}
      />
      <div className="JUNO_action_menu">
        <motion.div
          key={`action_menu_${moment.now()}`}
          onClick={onClickOpen}
          className={`action_menu_icon ${isOpen ? 'open' : ''}`}
        >
          <i className={`${FontAwesomeConfigurations.ICON_ELLIPSIS_VERTICAL} fa-fw`} />
        </motion.div>
        {isOpen && <div className={`action_menu_mobile_bg`}></div>}
        <motion.div
          className={`action_menu_dropdown ${isOpen ? 'open' : ''}`}
          variants={dropdown}
          initial={`hidden`}
          animate={isOpen ? 'visible' : 'hidden'}
          style={{ y: 12 }}
          ref={wrapperRef}
        >
          <JUNOActionMenu items={actionMenuItems} headers={actionMenuHeaders} />
        </motion.div>
      </div>
    </div>
  );
};
const dropdown = {
  hidden: {
    opacity: 0,
    transition: { duration: 0.15 },
    display: 'none',
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.15 },
    display: 'flex',
  },
};
export default NotificationPrefModule;
